import React, { useEffect, useState } from "react";
import { RiExpandUpDownLine } from "react-icons/ri";
import { Box, LinearProgress } from "@mui/material";
import { AgedPayablesReportModel_Resp } from "../../models/AgedPayablesReportModel";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import formatPrice from "../../helper/currencyFormatter";
import { formatUtcToLocal } from "../../helper/utils";
import { getRowsPerPageOptions } from "../AddEdit/CustomFunctions";
import { KeyboardArrowUp, KeyboardArrowDown } from "@mui/icons-material";

interface Iprops {
    ReportModuleCL: AgedPayablesReportModel_Resp;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>
    setPageSize: React.Dispatch<React.SetStateAction<number>>
    _pageSize: number
    filterModel: { items: { columnField: string, value: string, operatorValue: string }[], logicOperator?: 'and' | 'or' }
    setFilterModel: React.Dispatch<React.SetStateAction<{ items: { columnField: string, value: string, operatorValue: string }[], logicOperator?: 'and' | 'or' }>>
    setSortModel: React.Dispatch<React.SetStateAction<{ field: string, sort: string }[]>>
    sortModel: any
    loading: boolean
}

/** Status text colors in the grid (aligned with policy filing semantics — same palette as Reconciliation Queue). */
const RECONCILIATION_STATUS_COLORS: Record<string, string> = {
    Filed: '#1b5e20',
    'Filed - Binder': '#1b5e20',
    'Waiting on Client': '#f57f17',
    'Not Started': '#b71c1c',
    'Ready to File': '#1565c0',
};

export const AgedPayablesReportList: React.FC<Iprops> = ({
    ReportModuleCL,
    setCurrentPage,
    setPageSize,
    _pageSize,
    filterModel,
    setFilterModel,
    setSortModel,
    sortModel,
    loading
}) => {
    const [isLoading, setIsLoading] = useState(loading);

    useEffect(() => {
        setIsLoading(loading);
    }, [loading]);

    const sortArrows = (fieldName: string) => {
        const sortItem = sortModel?.find((item: any) => item.field === fieldName);
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc';
        return isSortedAsc
            ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px' }} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999' }} /></span>
            : isSortedDesc
                ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999' }} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px' }} /></span>
                : <span className=""><RiExpandUpDownLine /></span>;
    };

    const columns: GridColDef[] = [
        {
            field: "client", headerName: "Client", type: "string", minWidth: 200, headerAlign: "left", hideSortIcons: true, renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    {sortArrows('client')}
                </div>
            ), align: "left", flex: 3,
        },
        {
            field: "invoiceNumber", headerName: "Invoice Number", type: "string", minWidth: 170, headerAlign: "left", hideSortIcons: true, renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    {sortArrows('invoiceNumber')}
                </div>
            ), align: "left", flex: 3,
        },
        {
            field: "insuredName", headerName: "Insured Name", type: "string", minWidth: 200, headerAlign: "left", hideSortIcons: true, renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    {sortArrows('insuredName')}
                </div>
            ), align: "left", flex: 3,
        },
        {
            field: "policyNumber", headerName: "Policy Number", type: "string", minWidth: 200, headerAlign: "left", hideSortIcons: true, renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    {sortArrows('policyNumber')}
                </div>
            ), align: "left", flex: 3,
        },
        {
            field: "homeState", headerName: "Home State", type: "string", minWidth: 150, headerAlign: "left", hideSortIcons: true, renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    {sortArrows('homeState')}
                </div>
            ), align: "left", flex: 2,
        },
        {
            field: "taxType", headerName: "Tax Type", type: "string", minWidth: 170, headerAlign: "left", hideSortIcons: true, renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    {sortArrows('taxType')}
                </div>
            ), align: "left", flex: 3,
        },
        {
            field: "transactionType", headerName: "Transaction Type", type: "string", minWidth: 180, headerAlign: "left", hideSortIcons: true, renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    {sortArrows('transactionType')}
                </div>
            ), align: "left", flex: 3,
        },
        {
            field: "invDate",
            headerName: "Invoice Date",
            type: "date",
            headerAlign: "left",
            minWidth: 150,
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    {sortArrows('invDate')}
                </div>
            ),
            valueGetter: (params) => params.row.invDate ?? null,
            renderCell: (params) => formatUtcToLocal(params.row.invDate, "MM/DD/YYYY", true),
            align: "left",
            flex: 3,
        },
        {
            field: 'policyEffDate',
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    {sortArrows('policyEffDate')}
                </div>
            ),
            type: 'date',
            headerName: 'Policy Effective Date',
            minWidth: 220,
            flex: 4,
            align: 'left',
            headerAlign: 'left',
            valueGetter: (params) => params.row.policyEffDate ?? null,
            renderCell: (params) => formatUtcToLocal(params.row.policyEffDate, "MM/DD/YYYY", true),
        },
        {
            field: "policyExpDate",
            headerName: "Policy Expiration Date",
            type: "date",
            headerAlign: "left",
            minWidth: 210,
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    {sortArrows('policyExpDate')}
                </div>
            ),
            align: "left",
            flex: 4,
            valueGetter: (params) => params.row.policyExpDate ?? null,
            renderCell: (params) => formatUtcToLocal(params.row.policyExpDate, "MM/DD/YYYY", true),
        },
        {
            field: "grossPremium",
            headerName: "Gross Premium",
            type: "string",
            headerAlign: "right",
            minWidth: 170,
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title word-wrap-1 p-0">{params.colDef.headerName}</span>
                    {sortArrows('grossPremium')}
                </div>
            ),
            renderCell: (params) => {
                let grossPremium = params.row.grossPremium;
                let cleanedValue = grossPremium.replace(/[,\$]/g, "").trim();
                if (cleanedValue.startsWith("(") && cleanedValue.endsWith(")")) {
                    cleanedValue = "-" + cleanedValue.slice(1, -1);
                }
                const numericValue = parseFloat(cleanedValue);
                return isNaN(numericValue) ? grossPremium : `${formatPrice(numericValue.toString())}`;
            },
            align: "right",
            flex: 3,
        },
        {
            field: "taxAmt",
            headerName: "Tax Amount",
            type: "string",
            headerAlign: "right",
            minWidth: 170,
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    {sortArrows('taxAmt')}
                </div>
            ),
            renderCell: (params) => {
                let taxAmt = params.row.taxAmt;
                let cleanedValue = taxAmt.replace(/[,\$]/g, "").trim();
                if (cleanedValue.startsWith("(") && cleanedValue.endsWith(")")) {
                    cleanedValue = "-" + cleanedValue.slice(1, -1);
                }
                const numericValue = parseFloat(cleanedValue);
                return isNaN(numericValue) ? taxAmt : `${formatPrice(numericValue.toString())}`;
            },
            align: "right",
            flex: 3,
        },
        {
            field: "dateFiled",
            headerName: "Date Filed",
            type: "date",
            headerAlign: "left",
            minWidth: 130,
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    {sortArrows('dateFiled')}
                </div>
            ),
            align: "left",
            flex: 3,
            valueGetter: (params) => params.row.dateFiled ?? null,
            renderCell: (params) => formatUtcToLocal(params.row.dateFiled, "MM/DD/YYYY", true),
        },
        {
            field: "status", headerName: "Status", type: "string", headerAlign: "left", minWidth: 130, hideSortIcons: true, renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    {sortArrows('status')}
                </div>
            ),
            align: "left",
            flex: 3,
            renderCell: (params) => {
                const s = params.value == null ? '' : String(params.value);
                const color = RECONCILIATION_STATUS_COLORS[s];
                return (
                    <span style={color ? { color } : undefined}>{s}</span>
                );
            },
        },
    ];

    function EditToolbar() {
        return (
            <React.Fragment>
                <div style={{ position: "fixed", top: "0", left: "0", zIndex: "9990", width: "100%" }}>
                    {loading && <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />}
                </div>
            </React.Fragment>
        )
    }

    return (
        <React.Fragment>
            <Box
                sx={{
                    height: "calc(100vh - 365px)",
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    overflow: "hidden",
                    backgroundColor: "#ffffff",
                }}
            >
                <DataGridPro
                    rowHeight={56}
                    getRowId={(r) => r?.customerPolicyTaxesAndFeesId}
                    rows={ReportModuleCL.getData_Resp}
                    columns={columns}
                    loading={isLoading}
                    rowCount={ReportModuleCL.totalCount || 0}
                    rowsPerPageOptions={getRowsPerPageOptions(ReportModuleCL?.totalCount, _pageSize)}
                    pagination
                    pageSize={_pageSize}
                    paginationMode="server"
                    onPageChange={(newPage: number) => setCurrentPage(newPage + 1)}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    filterMode="server"
                    filterModel={filterModel}
                    onFilterModelChange={(newFilterModel: any) => setFilterModel(newFilterModel)}
                    sortingMode="server"
                    sortingOrder={['asc', 'desc']}
                    onSortModelChange={(newSortModel: any) => setSortModel(newSortModel)}
                    sortModel={sortModel}
                    style={{ textAlign: "center" }}
                    components={{
                        Toolbar: EditToolbar,
                    }}
                    sx={{
                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        border: "none",
                        fontSize: "14px",
                        color: "#475569",
                        backgroundColor: "#ffffff",
                        "& .MuiDataGridPro-columnHeaderTitle": {
                            whiteSpace: "normal",
                            lineHeight: 1.35,
                            fontWeight: 600,
                            fontSize: "12px",
                            letterSpacing: "0.05em",
                            textTransform: "uppercase",
                            color: "#64748b",
                        },
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: "#f8fafc",
                            borderBottom: "2px solid #e2e8f0",
                            borderTop: "2px solid #e2e8f0",
                            minHeight: "52px !important",
                            maxHeight: "52px !important",
                        },
                        "& .MuiDataGrid-columnHeader": {
                            "&:focus, &:focus-within": { outline: "none" },
                        },
                        "& .MuiDataGrid-row": {
                            transition: "background-color 0.15s ease",
                            "&:nth-of-type(even)": {
                                backgroundColor: "#fafbfc",
                            },
                            "&:hover": {
                                backgroundColor: "#f1f5f9 !important",
                            },
                            "&.Mui-selected": {
                                backgroundColor: "#ebf5fa !important",
                                "&:hover": {
                                    backgroundColor: "#dbeef7 !important",
                                },
                            },
                        },
                        "& .MuiDataGrid-cell": {
                            borderBottom: "1px solid #f1f5f9",
                            fontSize: "14px",
                            color: "#334155",
                            overflow: "visible",
                            whiteSpace: "normal",
                            lineHeight: "normal",
                            wordWrap: "break-word",
                            textOverflow: "clip",
                            padding: "8px 16px",
                            "&:focus": { outline: "none" },
                        },
                        "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                            marginBottom: "0px !important",
                        },
                        ".MuiDataGrid-virtualScroller": {
                            border: "none",
                        },
                        "& .MuiDataGrid-main": {
                            border: "none",
                        },
                        "& .MuiDataGrid-footerContainer": {
                            borderTop: "1px solid #e2e8f0",
                            backgroundColor: "#f8fafc",
                            minHeight: 56,
                        },
                        "& .MuiTablePagination-root": {
                            color: "#64748b",
                            fontSize: "13px",
                        },
                        "& .MuiTablePagination-select": {
                            fontSize: "13px",
                            borderRadius: "6px",
                            border: "1px solid #e2e8f0",
                        },
                        "& .MuiIconButton-root": {
                            color: "#64748b",
                            "&:hover": {
                                backgroundColor: "#f1f5f9",
                                color: "#475569",
                            },
                        },
                    }}
                    experimentalFeatures={{ newEditingApi: true }}
                />
            </Box>
        </React.Fragment>
    );
};
